import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams, HttpParamsOptions, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { retry, catchError, delay } from 'rxjs/operators';

import { environment } from '@env/environment';

import { map, tap } from 'rxjs/operators';

import { SortDirection } from '@angular/material/sort';

import { AuthQuery } from '@app/auth/auth.query';
import { DemoZahlungDataService } from './demo-zahlung-data.service';

export enum PaymentStatus {
  UNBEKANNT = 'U',           // Unknown - not yet processed
  ZUGEORDNET = 'Z',          // Assigned to mandate
  VOLLSTAENDIG = 'V',        // Fully paid
  TEILWEISE = 'T',           // Partially paid
  ÜBERZAHLT = 'Ü',           // Overpaid
  UNTERZAHLT = 'U',          // Underpaid
  DOPPELT = 'D',             // Double payment
  STORNIERT = 'S',           // Cancelled/Reversed
  ZURÜCKGEBUCHT = 'R'        // Returned/Refunded
}

export enum ReconciliationStatus {
  OFFEN = 'O',               // Open - needs processing
  GEPRÜFT = 'G',             // Checked - needs approval
  ABGESCHLOSSEN = 'A',       // Closed - fully reconciled
  PROBLEM = 'P',             // Problem - needs manual review
  ZURÜCKGEBUCHT = 'R'        // Returned to bank
}

export enum ImportStatus {
  IMPORTIERT = 'I',
  VERARBEITET = 'V',
  FEHLER = 'F',
  ABGESCHLOSSEN = 'A'
}

export interface Zahlung {
  // Primary Key
  id: number;
  
  // CAMT.054 Bank Data (from XML import)
  bank_transfer_id: string;           // AcctSvcrRef: "140002506202AEI-58PDN8000323"
  bank_tx_id: string;                 // TxId: "140002506202AEI-58PDN8000323"
  bank_msg_id: string;                // MsgId: "6000000510113853000961320250623"
  bank_stmt_id: string;               // Stmt Id: "6000000510113853000961320250623"
  
  // Amount Information
  betrag: number;                     // Amt: "30.00"
  currency: string;                   // Ccy: "EUR"
  credit_debit_indicator: string;     // CdtDbtInd: "CRDT" or "DBIT"
  
  // Dates
  valuta_datum: string;               // ValDt: "2025-06-23"
  buchungs_datum: string;             // BookgDt: "2025-06-23"
  import_datum: string;               // When we imported this
  
  // Bank Transaction Details
  bank_tx_code: string;               // BkTxCd: "PMNT/RCDT/ESCT"
  bank_tx_code_proprietary: string;   // Prtry: "166"
  bank_tx_code_issuer: string;        // Issr: "APC"
  
  // Parties Information
  debtor_name: string;                // Dbtr/Nm: "Ing. Florian Forstenpointner und Wa"
  debtor_iban: string;                // DbtrAcct/Id/IBAN: "AT592032032102873423"
  debtor_bic: string;                 // DbtrAgt/FinInstnId/BIC: "ASPKAT2LXXX"
  creditor_name: string;              // Cdtr/Nm: "Stadtgemeinde Mattighofen"
  creditor_iban: string;              // CdtrAcct/Id/IBAN: "AT106000000510113853"
  creditor_bic: string;               // CdtrAgt/FinInstnId/BIC: "BAWAATWWXXX"
  
  // Reference Information
  creditor_reference: string;         // RmtInf/Strd/CdtrRefInf/Ref: "523000017666"
  creditor_reference_type: string;    // RmtInf/Strd/CdtrRefInf/Tp/Cd: "SCOR"
  purpose_code: string;               // Purp/Prtry: "UEW"
  
  // Mandate Connection (our system)
  mandat_id: number;                  // Connected mandate ID
  aktenzeichen: string;               // Mandate reference number
  mandat_betrag_faellig: number;      // Original mandate amount
  mandat_faelligkeitsdatum: string;   // Mandate due date
  
  // Payment Status & Reconciliation
  payment_status: PaymentStatus;
  reconciliation_status: ReconciliationStatus;
  betrag_differenz: number;           // Difference between paid and due amount
  
  // System Fields
  import_batch_id: number;            // Which import batch this came from
  createdate: string;
  createtime: string;
  person: string;                     // Who processed this
  dienstnr_id: number;
  dienstnr_stvo: number;
  
  // Additional Info
  bemerkungen: string;
  processing_notes: string;
  is_duplicate: boolean;              // Flag for duplicate payments
  is_reversed: boolean;               // Flag for reversed transactions
}

export interface ImportBatch {
  id: number;
  filename: string;
  import_datum: string;
  import_zeit: string;
  person: string;
  dienstnr_id: number;
  status: ImportStatus;
  total_transfers: number;
  processed_transfers: number;
  error_count: number;
  bank_msg_id: string;
  bank_stmt_id: string;
  account_iban: string;
  account_balance: number;
  currency: string;
}

export interface ZahlungTable {
  data: Zahlung[];
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
  summary: {
    total_betrag: number;
    total_offen: number;
    total_abgeschlossen: number;
    total_probleme: number;
  };
}

export interface ZahlungSingle {
  data: Zahlung;
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
  related_mandates: any[];  // Related mandates for selection
  payment_history: Zahlung[];  // Other payments for same mandate
  lifecycle?: JournalEntry[];  // Full mandate journal (by aktenzeichen)
}

export interface JournalEntry {
  journalid?: number;
  userid?: number;
  aktion?: string;
  info?: string;
  category?: string;
  created?: string;
  aktenzeichen?: string;
  dienstname?: string;
}

export interface ZahlungFilter {
  dateFrom?: string;
  dateTo?: string;
  paymentStatus?: PaymentStatus[];
  reconciliationStatus?: ReconciliationStatus[];
  mandatId?: number;
  aktenzeichen?: string;
  debtorName?: string;
  amountMin?: number;
  amountMax?: number;
  importBatchId?: number;
}

export interface MatchResult {
  zahlung_id: number;
  mandat_id: number;
  confidence: number;
  match_type: string;
  reason: string;
}

export interface ReconciliationResult {
  success: boolean;
  zahlung_id: number;
  mandat_id: number;
  new_status: PaymentStatus;
  differenz: number;
  message: string;
}

export interface PaymentSummary {
  total_payments: number;
  total_amount: number;
  vollstaendig: number;
  teilweise: number;
  ueberzahlt: number;
  unterzahlt: number;
  doppelt: number;
  unbekannt: number;
}

export interface ReturnResult {
  success: boolean;
  processed_count: number;
  errors: string[];
}

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class ZahlungsabgleichService {
  errorMsg: string = '';

  constructor(
    private httpClient: HttpClient, 
    private authQuery: AuthQuery,
    private demoDataService: DemoZahlungDataService
  ) {}

  /**
   * Import one or more bank files (.dat / .xml / .zip) via multipart upload.
   * source: Manual (file picker) | Drop (drag-drop) | Sync (inbox)
   * Returns { result, batch_id, summary }.
   * NOTE: do NOT set a JSON Content-Type here - the browser must set the
   * multipart boundary. The JWT interceptor still adds the Bearer token.
   */
  importFiles(files: File[], source: 'Manual' | 'Drop' | 'Sync' = 'Manual'): Observable<any> {
    const formData = new FormData();
    files.forEach((f, i) => formData.append('file' + i, f, f.name));
    formData.append('source', source);

    const auth = this.getAuthData();
    Object.keys(auth).forEach((k) => formData.append(k, '' + ((auth as any)[k] ?? '')));

    return this.httpClient.post(`${environment.serverUrl}/zahlung-import`, formData);
  }

  /**
   * Trigger a server-side scan of the Retourdatenträger inbox folder.
   */
  syncInbox(): Observable<any> {
    return this.httpClient.post(
      `${environment.serverUrl}/zahlung-sync`,
      JSON.stringify({ ...this.getAuthData() }),
      httpOptions
    );
  }

  /**
   * Import CAMT.054 file (single-file convenience wrapper).
   */
  importCAMT054(file: File): Observable<any> {
    return this.importFiles([file], 'Manual');
  }

  /**
   * Process import batch
   */
  processImportBatch(batchId: number): Observable<Zahlung[]> {
    const data = {
      batch_id: batchId,
      ...this.getAuthData()
    };

    return this.httpClient.post<Zahlung[]>(`${environment.serverUrl}/zahlung-process-batch`, JSON.stringify(data), httpOptions);
  }

  /**
   * GET ALL Zahlungen with filtering
   */
  getZahlungen(
    pageNumber: number,
    pageSize: number,
    sort: string,
    order: SortDirection,
    searchTerm: string,
    filters: ZahlungFilter
  ): Observable<ZahlungTable> {
    const data = {
      ...this.getAuthData(),
      page: pageNumber,
      per_page: pageSize,
      sort: sort,
      order: order,
      search: searchTerm || '',
      filters: JSON.stringify(filters || {}),
    };

    return this.httpClient.post<ZahlungTable>(
      `${environment.serverUrl}/zahlungen`,
      JSON.stringify(data),
      httpOptions
    );
  }

  /**
   * GET / FIND BY ID
   */
  getZahlungById(id: number): Observable<ZahlungSingle> {
    return this.httpClient.get<ZahlungSingle>(`${environment.serverUrl}/zahlung/${id}`, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
      }),
    });
  }

  /**
   * UPDATE Zahlung
   */
  updateZahlung(zahlung: Zahlung): Observable<Zahlung> {
    const data = {
      ...this.getAuthData(),
      zahlung: JSON.stringify(zahlung),
      id: zahlung.id,
    };

    return this.httpClient.post<Zahlung>(`${environment.serverUrl}/zahlung-update`, JSON.stringify(data), httpOptions).pipe(
      map((response) => {
        return {
          ...response,
        };
      }),
      tap((response: Zahlung) => {
        console.log('Zahlung updated:', response);
      }),
      catchError((error) => {
        let errorMsg: string = '';
        if (error.error instanceof ErrorEvent) {
          this.errorMsg = `Error: ${error.error.message}`;
        } else {
          this.errorMsg = this.getServerErrorMessage(error);
        }
        return throwError(errorMsg);
      })
    );
  }

  /**
   * Assign Zahlung to Mandate
   */
  assignToMandate(zahlungId: number, mandatId: number): Observable<Zahlung> {
    const data = {
      ...this.getAuthData(),
      zahlung_id: zahlungId,
      mandat_id: mandatId,
    };

    return this.httpClient.post<Zahlung>(`${environment.serverUrl}/zahlung-assign`, JSON.stringify(data), httpOptions).pipe(
      map((response) => {
        return {
          ...response,
        };
      }),
      catchError((error) => {
        let errorMsg: string = '';
        if (error.error instanceof ErrorEvent) {
          this.errorMsg = `Error: ${error.error.message}`;
        } else {
          this.errorMsg = this.getServerErrorMessage(error);
        }
        return throwError(errorMsg);
      })
    );
  }

  /**
   * Auto match payments
   */
  autoMatchPayments(): Observable<MatchResult[]> {
    const data = {
      ...this.getAuthData(),
    };

    return this.httpClient.post<MatchResult[]>(`${environment.serverUrl}/zahlung-auto-match`, JSON.stringify(data), httpOptions);
  }

  /**
   * Reconcile payment
   */
  reconcilePayment(zahlungId: number): Observable<ReconciliationResult> {
    const data = {
      ...this.getAuthData(),
      zahlung_id: zahlungId,
    };

    return this.httpClient.post<ReconciliationResult>(`${environment.serverUrl}/zahlung-reconcile`, JSON.stringify(data), httpOptions);
  }

  /**
   * Get unassigned payments
   */
  getUnassignedPayments(): Observable<Zahlung[]> {
    const apiUrl = `${environment.serverUrl}/zahlungen-unassigned`;

    return this.httpClient.get<Zahlung[]>(apiUrl, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
      }),
    });
  }

  /**
   * Get payment summary
   */
  getPaymentSummary(dateFrom: string, dateTo: string): Observable<PaymentSummary> {
    const apiUrl = `${environment.serverUrl}/zahlungen-summary`;

    return this.httpClient.get<PaymentSummary>(apiUrl, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
      }),
      params: new HttpParams()
        .set('dateFrom', dateFrom)
        .set('dateTo', dateTo),
    });
  }

  /**
   * Build the query params for the payment report (date range or aktenzeichen).
   */
  private buildReportParams(opts: { dateFrom?: string; dateTo?: string; aktenzeichen?: string }, format: 'html' | 'pdf'): HttpParams {
    let params = new HttpParams().set('format', format);
    if (opts.aktenzeichen) {
      params = params.set('aktenzeichen', opts.aktenzeichen);
    } else {
      if (opts.dateFrom) params = params.set('dateFrom', opts.dateFrom);
      if (opts.dateTo) params = params.set('dateTo', opts.dateTo);
    }
    // include the logged-in user for the "gedruckt von" footer
    const user = this.authQuery.getValue().dienstname || '';
    if (user) params = params.set('user', '' + user);
    // apikey for the API guard (Bearer is added by the interceptor as well)
    params = params.set('apikey', '' + environment.api_key);
    return params;
  }

  /**
   * Get the payment report as rendered HTML (for the in-app preview).
   */
  getReportHtml(opts: { dateFrom?: string; dateTo?: string; aktenzeichen?: string }): Observable<string> {
    const apiUrl = `${environment.serverUrl}/zahlungen-report`;
    return this.httpClient.get(apiUrl, {
      params: this.buildReportParams(opts, 'html'),
      responseType: 'text',
    });
  }

  /**
   * Get the payment report as a PDF blob (for download).
   */
  getReportPdf(opts: { dateFrom?: string; dateTo?: string; aktenzeichen?: string }): Observable<Blob> {
    const apiUrl = `${environment.serverUrl}/zahlungen-report`;
    return this.httpClient.get(apiUrl, {
      params: this.buildReportParams(opts, 'pdf'),
      responseType: 'blob',
    });
  }

  /**
   * Export reconciliation report (kept for backwards compatibility) -> now the PDF report.
   */
  exportReconciliationReport(filters: ZahlungFilter): Observable<Blob> {
    return this.getReportPdf({
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      aktenzeichen: filters.aktenzeichen,
    });
  }

  /**
   * Mark payment for return
   */
  markForReturn(zahlungId: number, reason: string): Observable<Zahlung> {
    const data = {
      ...this.getAuthData(),
      zahlung_id: zahlungId,
      reason: reason,
    };

    return this.httpClient.post<Zahlung>(`${environment.serverUrl}/zahlung-mark-return`, JSON.stringify(data), httpOptions);
  }

  /**
   * Process returns
   */
  processReturns(returnIds: number[]): Observable<ReturnResult> {
    const data = {
      ...this.getAuthData(),
      return_ids: returnIds,
    };

    return this.httpClient.post<ReturnResult>(`${environment.serverUrl}/zahlung-process-returns`, JSON.stringify(data), httpOptions);
  }

  /**
   * Export a SINGLE camt.054 retour file for one payment (no Sammeldatei).
   * The backend marks the payment for return (idempotent) and streams the file.
   */
  exportSingleReturn(zahlungId: number, reason: string): Observable<Blob> {
    const data = {
      ...this.getAuthData(),
      zahlung_id: zahlungId,
      reason: reason,
    };

    return this.httpClient.post(`${environment.serverUrl}/zahlung-retour-export-single`, JSON.stringify(data), {
      ...httpOptions,
      responseType: 'blob',
    });
  }

  /**
   * Download a generated retour camt.054 file by packet id.
   */
  downloadRetour(packetId: number): Observable<Blob> {
    const data = {
      ...this.getAuthData(),
      pakid: packetId,
    };

    return this.httpClient.post(`${environment.serverUrl}/zahlung-retour-download`, JSON.stringify(data), {
      ...httpOptions,
      responseType: 'blob',
    });
  }

  /**
   * Unassign a payment from its mandate (revert reconciliation).
   */
  unassignZahlung(zahlungId: number): Observable<any> {
    const data = {
      ...this.getAuthData(),
      zahlung_id: zahlungId,
    };

    return this.httpClient.post(`${environment.serverUrl}/zahlung-unassign`, JSON.stringify(data), httpOptions);
  }

  /**
   * DELETE Zahlung
   */
  deleteZahlung(id: number): Observable<boolean> {
    const data = {
      ...this.getAuthData(),
      id: id,
    };

    return this.httpClient.post<boolean>(`${environment.serverUrl}/zahlung-delete`, JSON.stringify(data), httpOptions);
  }

  /**
   * Get authentication data
   */
  private getAuthData() {
    return {
      apikey: '' + environment.api_key,
      phone_id: this.authQuery.getValue().phone_id,
      token: this.authQuery.getValue().access_token,
      akey: '' + environment.api_key,
      did: this.authQuery.getValue().dienstnr,
      didstvo: this.authQuery.getValue().dienstnr_stvo,
      didname: this.authQuery.getValue().dienstname,
    };
  }

  /**
   * Error handling
   */
  private getServerErrorMessage(error: HttpErrorResponse) {
    console.log('custom service error handler');

    switch (error.status) {
      case 0: {
        return `An error occurred: ${error.error}`;
      }
      case 404: {
        return `Not Found: ${error.message}`;
      }
      case 403: {
        return `Access Denied: ${error.message}`;
      }
      case 500: {
        return `Internal Server Error: ${error.message}`;
      }
      default: {
        return `Unknown Server Error: ${error.error}`;
      }
    }
  }
}
